import React, { useRef } from 'react';
import { useScroll, useTransform, motion } from 'framer-motion';
import { 
  Cloud, Lock, Zap,
  Instagram, Video, MousePointer,
  Code, Smartphone, Globe,
  Wind, Thermometer
} from 'lucide-react';

const services = [
  {
    id: 1,
    title: "SaaS",
    description: "Sistemas como Serviço.",
    color: "#10B981",
    textColor: "text-[#10B981]",
    gif: "/Saas.gif",
    ingredients: [
      { icon: Cloud,      label: "Cloud",    value: "100%" },
      { icon: Lock,       label: "Segurança", value: "SSL" },
      { icon: Zap,        label: "Speed",    value: "Fast" },
    ],
    nutrients: [
      { label: "Uptime",     value: "99.9%" },
      { label: "Integração", value: "API REST" },
      { label: "Suporte",    value: "24/7" },
    ]
  },
  {
    id: 2,
    title: "Gestão de Mídias",
    description: "Estratégia Visual.",
    color: "#db2777",
    textColor: "text-[#db2777]",
    gif: "/gestaomidia.gif",
    ingredients: [
      { icon: Instagram,    label: "Social",  value: "Reels" },
      { icon: Video,        label: "Edição",  value: "4K" },
      { icon: MousePointer, label: "Ads",     value: "ROI" },
    ],
    nutrients: [
      { label: "Engajamento", value: "+300%" },
      { label: "Conversão",   value: "Alta" },
      { label: "Branding",    value: "Premium" },
    ]
  },
  {
    id: 3,
    title: "Desenvolvimento",
    description: "Soluções Sob Medida.",
    color: "#3B82F6",
    textColor: "text-[#3B82F6]",
    gif: "/desenvolvimento.gif",
    ingredients: [
      { icon: Code,       label: "Code",   value: "Clean" },
      { icon: Smartphone, label: "Mobile", value: "Native" },
      { icon: Globe,      label: "Web",    value: "React" },
    ],
    nutrients: [
      { label: "Tech Stack",  value: "Modern" },
      { label: "Design",      value: "UI/UX" },
      { label: "Performance", value: "Max" },
    ]
  }
];

// ─────────────────────────────────────────────────────────────────
// Each service gets its OWN 200vh sticky section.
// Within that section scrollYProgress goes 0→1 for that slide alone.
// Fade in: 0→0.25, Stable: 0.25→0.75, Fade out: 0.75→1
// (first slide starts at opacity 1; last slide stays at opacity 1)
// ─────────────────────────────────────────────────────────────────

function StickyServiceSlide({ service, isFirst, isLast }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });

  // Opacity: fade in / stable / fade out
  const opacity = useTransform(
    scrollYProgress,
    isFirst
      ? [0,    0.75, 1]
      : isLast
      ? [0, 0.25,    1]
      : [0, 0.25, 0.75, 1],
    isFirst
      ? [1,    1,    0]
      : isLast
      ? [0,    1,    1]
      : [0,    1,    1,    0]
  );

  // Left text: slide in from left (fade-in phase), slide out left (fade-out phase)
  const leftX = useTransform(
    scrollYProgress,
    isFirst
      ? [0, 0.75, 1]
      : isLast
      ? [0, 0.25, 1]
      : [0, 0.25, 0.75, 1],
    isFirst
      ? ['0%',   '0%',   '-60%']
      : isLast
      ? ['-60%', '0%',   '0%']
      : ['-60%', '0%',   '0%',   '-60%']
  );

  // Right text: slide in from right
  const rightX = useTransform(
    scrollYProgress,
    isFirst
      ? [0, 0.75, 1]
      : isLast
      ? [0, 0.25, 1]
      : [0, 0.25, 0.75, 1],
    isFirst
      ? ['0%',  '0%',  '60%']
      : isLast
      ? ['60%', '0%',  '0%']
      : ['60%', '0%',  '0%',  '60%']
  );

  // GIF: slide up from below / slide up on exit
  const gifY = useTransform(
    scrollYProgress,
    isFirst
      ? [0, 0.75, 1]
      : isLast
      ? [0, 0.25, 1]
      : [0, 0.25, 0.75, 1],
    isFirst
      ? [0,    0,   -300]
      : isLast
      ? [300,  0,      0]
      : [300,  0,      0,  -300]
  );

  return (
    <div ref={ref} className="relative h-[200vh]">
      <div className="sticky top-0 h-screen flex overflow-hidden">

        {/* Split background */}
        <div className="absolute inset-0 flex">
          {/* Left colored panel */}
          <div
            className="w-1/2 h-full relative overflow-hidden"
            style={{ backgroundColor: service.color }}
          >
            <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none select-none">
              <h1 className="text-[20vw] font-bold text-white leading-none rotate-90">VITA</h1>
            </div>
          </div>

          {/* Right white panel */}
          <div className="w-1/2 h-full bg-slate-50 relative overflow-hidden">
            <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 w-[40vw] h-[40vw] border-4 border-gray-100 rounded-full opacity-40" />
            <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 w-[26vw] h-[26vw] border-4 border-gray-100 rounded-full opacity-40" />
            <div className="absolute top-8 right-8 flex gap-2">
              <div className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-300"><Wind size={14} /></div>
              <div className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-300"><Thermometer size={14} /></div>
            </div>
          </div>
        </div>

        {/* ── Left content ── */}
        <motion.div
          style={{ x: leftX, opacity }}
          className="absolute left-0 top-0 w-1/2 h-full z-20 flex flex-col justify-center px-12"
        >
          <div className="max-w-lg mx-auto w-full">
            <h2 className="text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight">
              {service.title}
            </h2>
            <p className="text-white/90 text-xl font-light mb-10 max-w-sm">{service.description}</p>
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-3 text-white/70 uppercase text-xs tracking-widest mb-2 border-b border-white/20 pb-2">
                Soluções
              </div>
              {service.ingredients.map((ing, i) => (
                <div key={i} className="flex flex-col items-center text-white">
                  <ing.icon className="w-8 h-8 mb-2 opacity-80" />
                  <span className="text-sm font-bold">{ing.label}</span>
                  <span className="text-xs opacity-70">{ing.value}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ── Right content ── */}
        <motion.div
          style={{ x: rightX, opacity }}
          className="absolute right-0 top-0 w-1/2 h-full z-20 flex flex-col justify-center px-12 pointer-events-none"
        >
          <div className="max-w-xs mx-auto w-full text-right pointer-events-auto">
            <div className="mb-10">
              <h3 className={`text-4xl font-bold ${service.textColor} mb-1`}>{service.nutrients[0].value}</h3>
              <p className="text-gray-500 uppercase tracking-widest text-sm">{service.nutrients[0].label}</p>
            </div>
            <div className="space-y-4">
              <h4 className="text-gray-400 uppercase tracking-widest text-xs border-b border-gray-200 pb-2">
                Nutrientes
              </h4>
              {service.nutrients.slice(1).map((nut, i) => (
                <div key={i} className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                  <span className="text-gray-400 text-sm font-medium uppercase">{nut.label}</span>
                  <span className={`font-bold ${service.textColor}`}>{nut.value}</span>
                </div>
              ))}
            </div>
            <button
              className={`mt-8 px-8 py-3 rounded-full text-white font-bold shadow-lg hover:scale-105 transition-transform ${service.textColor.replace('text-', 'bg-')}`}
            >
              SAIBA MAIS
            </button>
          </div>
        </motion.div>

        {/* ── Center GIF ── */}
        <motion.div
          style={{ y: gifY, opacity }}
          className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none"
        >
          <div className="absolute w-48 h-72 bg-black/20 blur-2xl rounded-full translate-y-8" />
          <img
            src={service.gif}
            alt={service.title}
            className="relative h-[58vh] max-w-[28vw] object-cover rounded-[3rem] shadow-2xl"
          />
        </motion.div>

      </div>
    </div>
  );
}

// ─── Mobile: stacked cards ────────────────────────────────────────
function MobileServices() {
  return (
    <div className="flex flex-col">
      {services.map((service) => (
        <div
          key={service.id}
          className="relative flex flex-col items-center px-6 py-16 overflow-hidden"
          style={{ backgroundColor: service.color }}
        >
          <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none select-none">
            <span className="text-[40vw] font-bold text-white leading-none">VITA</span>
          </div>

          <div className="relative z-10 text-center mb-8">
            <h2 className="text-4xl font-bold text-white mb-2">{service.title}</h2>
            <p className="text-white/90 text-base">{service.description}</p>
          </div>

          <div className="relative z-10 mb-8">
            <img
              src={service.gif}
              alt={service.title}
              className="h-48 object-cover rounded-3xl shadow-2xl"
            />
          </div>

          <div className="relative z-10 w-full max-w-xs">
            <p className="text-white/70 uppercase text-xs tracking-widest mb-3 border-b border-white/20 pb-2">Soluções</p>
            <div className="grid grid-cols-3 gap-3 mb-8">
              {service.ingredients.map((ing, i) => (
                <div key={i} className="flex flex-col items-center text-white">
                  <ing.icon className="w-6 h-6 mb-1 opacity-80" />
                  <span className="text-xs font-bold">{ing.label}</span>
                  <span className="text-xs opacity-60">{ing.value}</span>
                </div>
              ))}
            </div>

            <div className="bg-white/10 rounded-2xl p-4">
              <p className="text-white/70 uppercase text-xs tracking-widest mb-3">Nutrientes</p>
              {service.nutrients.map((nut, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-white/10 last:border-0">
                  <span className="text-white/70 text-sm">{nut.label}</span>
                  <span className="text-white font-bold text-sm">{nut.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────
export function ServicesStickyScroll() {
  return (
    <>
      {/* Mobile */}
      <div className="block md:hidden">
        <MobileServices />
      </div>

      {/* Desktop – each service has its own 200vh sticky section */}
      <div className="hidden md:block">
        {services.map((service, index) => (
          <StickyServiceSlide
            key={service.id}
            service={service}
            isFirst={index === 0}
            isLast={index === services.length - 1}
          />
        ))}
      </div>
    </>
  );
}
